import React, { useState, useMemo } from "react";
import { useRoleVerify } from "Common/RoleVerify";
import { GetAll, Delete } from "adminApi/PromoCodeApi/Promocode";
import DataTable from "Common/DataTable/DataTable";
import useDebouncedValue from "Common/DataTable/useDebouncedValue";
import usePaginatedFetch from "Hooks/usePaginatedFetch";
import useTableConfig from "Hooks/useTableConfig";
import { generateDisplayFields } from "utils/fieldMapper";
import fields from "../../json/PromoCodeFields.json";
import { FilterStatus } from "utils/FilterStatus";

const PromoCodeTable = () => {
  const [search, setSearch] = useState("");
  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(1);
  const debouncedSearch = useDebouncedValue(search, 500);
  const canShowAction = useRoleVerify("", ["can_edit", "can_delete"]);
  const sizeOptions = [10, 20, 50, 100];
  const { tabledata } = fields;

  // Fetch Hook
  const { data, meta, loading, error, refetch } = usePaginatedFetch(GetAll, {
    page,
    limit,
    search: debouncedSearch,
    title: "Promo Code List",
  });

  const displayFields = generateDisplayFields(tabledata);

  // Memoized: format dates and status badge — guarded against null dates
  const formattedData = useMemo(() => {
    return FilterStatus(data).map((item) => ({
      ...item,
      start_date: item.start_date
        ? new Date(item.start_date).toLocaleDateString()
        : "-",
      end_date: item.end_date
        ? new Date(item.end_date).toLocaleDateString()
        : "-",
    }));
  }, [data]);

  // Table Hook
  const { formattedData: tableRows, columns } = useTableConfig({
    data: formattedData,
    page,
    limit,
    canShowAction,
    refetch,
    IsDelete: true,
    config: {
      entityName: "Promo Code",
      editUrl: "edit-promo-code",
      idKey: "promo_code_id",
      deleteApi: Delete,
      displayFields,
    },
  });

  const handleSearch = (val, resetPage = null) => {
    setSearch(val);
    if (resetPage !== null) setPage(resetPage);
  };

  const onPageChange = (p) => setPage(p);
  const onLimitChange = (newLimit) => {
    setLimit(newLimit);
    setPage(1);
  };

  return (
    <DataTable
      columns={columns}
      data={tableRows}
      options={{
        loading,
        error,
        sortableColumns: displayFields.map((f) => f.accessor),
        copy: true,
        csv: true,
        pdf: true,
        print: true,
      }}
      Search={{
        search: true,
        SearchInputValue: search,
        handleSearch: handleSearch,
      }}
      Entries={{
        Entries: true,
        limit,
        onLimitChange,
        options: sizeOptions,
      }}
      pagination={{
        isActive: true,
        meta,
        onPageChange,
      }}
    />
  );
};

export default PromoCodeTable;
